/**
 * flowHistory — canvas undo/redo, as pure logic.
 *
 * ## Model
 *
 * The canvas state is mutated from dozens of places: React Flow's change
 * callbacks, ~40 direct `setNodes` sites in the editor, every generation
 * handler in NodeAdapters, GenerationLifecycleService (with no component
 * mounted at all), upload completions, effects that re-measure media. Only a
 * small subset of those are *user actions*. So history is not recorded on
 * `setNodes`; it is recorded at user-action boundaries:
 *
 *   1. A user-action entry point calls `checkpoint(current, label)` BEFORE it
 *      mutates. That captures `before` (a reference to the current arrays —
 *      React state is immutable, so no copy is needed) and opens a window.
 *   2. Every commit is passed to `observe(current)`. The first commit whose
 *      graph differs from `before` (ignoring selection, measurement and
 *      generation-run flags) is the action's effect: it becomes `after` and
 *      the entry is pushed. A window that sees no change is dropped, so a
 *      no-op click never becomes an empty step.
 *   3. Continuous actions keep refreshing `after` while they are live: a drag
 *      (`hold` until `release`), a typing burst or slider scrub (same
 *      `coalesceKey`, <= `coalesceMs` idle between touches).
 *
 * ## Undo is a three-way merge, not a snapshot restore
 *
 * Undo applies the transition `after -> before` to the CURRENT graph, and redo
 * applies `before -> after`. For every field the action changed:
 *
 *   - the field is set back only if its current value is still what the action
 *     left (`current == after`). If anything changed it since — a generation
 *     writing its result, an upload swapping a blob URL for the R2 one, a fan
 *     re-layout moving an output node — the current value wins.
 *   - fields the action did not change are never touched.
 *   - nodes and edges that exist only in the current graph (created after the
 *     action — e.g. Output nodes spawned by a generation) are never touched.
 *
 * Nodes the transition removes (undo of "add", redo of "delete") are removed
 * only if they have not acquired a generation result since: a node whose
 * result signature (pages count, generated media URL, …) differs from the one
 * it had when the action ended is kept. So undo never deletes a result the user
 * paid for, and generation results never create history entries.
 *
 * `after` captured during a live refresh keeps the RESULT_DATA_KEYS it had
 * when the entry was first closed, so a generation that lands mid-drag is not
 * mistaken for part of the drag.
 */

import type { Node, Edge } from '@xyflow/react';

export interface FlowGraph {
  nodes: Node[];
  edges: Edge[];
}

export interface CheckpointOptions {
  /** Consecutive checkpoints with the same key (and redo empty) merge into one step. */
  coalesceKey?: string;
  /** Keep the step live (coalescing + refreshing `after`) until `release(coalesceKey)`. */
  hold?: boolean;
  /**
   * Ids of the nodes whose result-carrying fields the action itself edits
   * (deleting an output page, editing an output's text, setting/removing a
   * container image, switching node type). Result fields of every OTHER node
   * stay frozen out of the step, so a generation that lands inside the window
   * can never be attributed to it.
   */
  touchesResults?: readonly string[];
}

export interface HistoryEntry {
  id: number;
  label: string;
  coalesceKey?: string;
  before: FlowGraph;
  after: FlowGraph;
  /** Node ids present in `after` when the entry closed — fixed for live refreshes. */
  afterNodeIds: Set<string>;
  afterEdgeIds: Set<string>;
  lastTouch: number;
  hold: boolean;
  touchesResults: Set<string>;
  /** No more coalescing into this entry. */
  sealed: boolean;
  /** `after` keeps refreshing until this time (grace for a drag's final commit). */
  liveUntil: number;
}

interface PendingEntry {
  id: number;
  label: string;
  coalesceKey?: string;
  before: FlowGraph;
  openedAt: number;
  hold: boolean;
  touchesResults: Set<string>;
}

export interface FlowHistoryOptions {
  limit?: number;
  coalesceMs?: number;
  /**
   * An un-held window that has seen no change after this long is dropped: the
   * action's own commit lands within a frame or two, so anything later is not
   * the action's effect.
   */
  windowMs?: number;
  /** A held window that never saw a change is dropped after this long regardless. */
  maxHoldMs?: number;
  /** Extra "a generation is running on this node" signal (lifecycle registry, flow runner). */
  isRunning?: (nodeId: string) => boolean;
  now?: () => number;
  /** Whether a React Flow node type is a paid generator (see result signature). */
  isGenerator?: (type: string | undefined) => boolean;
}

export interface ApplyResult {
  graph: FlowGraph;
  entry: HistoryEntry;
  /** The transition to re-apply against live state (functional updaters). */
  base: FlowGraph;
  target: FlowGraph;
  /**
   * The step would remove a node whose generation is in flight. Nothing was
   * applied and the stacks did not move: undo stops here (the caller says so).
   */
  blocked?: boolean;
}

// ---------------------------------------------------------------------------
// Field classification
// ---------------------------------------------------------------------------

/** Node-level keys that are view state, React Flow bookkeeping or render decoration. */
export const TRANSIENT_NODE_KEYS: ReadonlySet<string> = new Set([
  'measured', 'selected', 'dragging', 'resizing', 'className', 'internals',
  'computed', 'positionAbsolute', 'handleBounds', '__rf',
]);

/**
 * Data keys owned by a generation RUN (spinners, progress, job bookkeeping) or
 * injected at render. They never count as a user change and undo never writes
 * them.
 */
export const TRANSIENT_DATA_KEYS: ReadonlySet<string> = new Set([
  'isProcessing', 'isGenerating', 'streamingInProgress', 'batchProgress',
  'batchCancelRequested', 'error', 'isCondensing', 'isUploading', 'uploadFailed',
  'jobId', 'jobStatus', 'definition',
]);

/**
 * Data keys that carry a generation RESULT. They are ordinary fields for the
 * three-way merge (a user action that changed one — e.g. deleting a page — is
 * undoable as long as nothing has changed it since), but a live refresh never
 * picks up new values for them.
 */
export const RESULT_DATA_KEYS: ReadonlySet<string> = new Set([
  'pages', 'currentPageIndex', 'output', 'content', 'context', 'outputsMediaType',
  'imageUrl', 'videoUrl', 'audioUrl', 'revisedPrompt', 'outputDimensions',
  'lastResult', 'lastExecuted', 'transcription', 'threadId', 'currentAspectRatio',
]);

const EDGE_TRANSIENT_KEYS: ReadonlySet<string> = new Set(['selected', 'className']);

const MAX_DEPTH = 8;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/**
 * Structural equality that ignores function values (handlers are re-created
 * freely) and short-circuits on reference equality, which is the common case
 * for React state that was spread rather than rebuilt.
 */
export function deepEqual(a: unknown, b: unknown, depth = 0): boolean {
  if (a === b) return true;
  if (typeof a === 'function' && typeof b === 'function') return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    // NaN
    return a !== a && b !== b;
  }
  if (depth > MAX_DEPTH) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i], depth + 1)) return false;
    return true;
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const ka = Object.keys(a).filter((k) => typeof (a as any)[k] !== 'function' && (a as any)[k] !== undefined);
  const kb = Object.keys(b).filter((k) => typeof (b as any)[k] !== 'function' && (b as any)[k] !== undefined);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!deepEqual((a as any)[k], (b as any)[k], depth + 1)) return false;
  }
  return true;
}

function userDataKeys(data: Record<string, unknown> | undefined): string[] {
  if (!data) return [];
  return Object.keys(data).filter(
    (k) => !TRANSIENT_DATA_KEYS.has(k) && typeof data[k] !== 'function',
  );
}

function userNodeKeys(node: Node): string[] {
  return Object.keys(node).filter(
    (k) => k !== 'data' && k !== 'id' && !TRANSIENT_NODE_KEYS.has(k) && typeof (node as any)[k] !== 'function',
  );
}

/** True when two versions of the same node differ in anything a user owns. */
export function nodesDiffer(a: Node, b: Node): boolean {
  if (a === b) return false;
  const keys = new Set([...userNodeKeys(a), ...userNodeKeys(b)]);
  for (const k of keys) if (!deepEqual((a as any)[k], (b as any)[k])) return true;
  const da = (a.data || {}) as Record<string, unknown>;
  const db = (b.data || {}) as Record<string, unknown>;
  if (da === db) return false;
  const dkeys = new Set([...userDataKeys(da), ...userDataKeys(db)]);
  for (const k of dkeys) if (!deepEqual(da[k], db[k])) return true;
  return false;
}

function edgeKeys(e: Edge): string[] {
  return Object.keys(e).filter((k) => k !== 'id' && !EDGE_TRANSIENT_KEYS.has(k) && typeof (e as any)[k] !== 'function');
}

export function edgesDiffer(a: Edge, b: Edge): boolean {
  if (a === b) return false;
  const keys = new Set([...edgeKeys(a), ...edgeKeys(b)]);
  for (const k of keys) if (!deepEqual((a as any)[k], (b as any)[k])) return true;
  return false;
}

/** Graph equality over user-owned state only (order-insensitive). */
export function graphsEqual(a: FlowGraph, b: FlowGraph): boolean {
  if (a.nodes === b.nodes && a.edges === b.edges) return true;
  if (a.nodes.length !== b.nodes.length || a.edges.length !== b.edges.length) return false;
  if (a.nodes !== b.nodes) {
    const bm = new Map(b.nodes.map((n) => [n.id, n]));
    for (const n of a.nodes) {
      const o = bm.get(n.id);
      if (!o || nodesDiffer(n, o)) return false;
    }
  }
  if (a.edges !== b.edges) {
    const bm = new Map(b.edges.map((e) => [e.id, e]));
    for (const e of a.edges) {
      const o = bm.get(e.id);
      if (!o || edgesDiffer(e, o)) return false;
    }
  }
  return true;
}

/**
 * What counts as "this node holds a generation result". Output/container nodes
 * are judged by how many result pages they hold (a pasted video swapping its
 * blob URL for the R2 URL is not a new result); generator nodes additionally by
 * the media / text their run wrote onto them.
 */
export function resultSignature(node: Node, isGenerator: (t: string | undefined) => boolean): string {
  const d = (node.data || {}) as Record<string, unknown>;
  const pagesLen = Array.isArray(d.pages) ? d.pages.length : 0;
  if (!isGenerator(node.type)) return `p${pagesLen}`;
  const pick = (k: string) => (typeof d[k] === 'string' || typeof d[k] === 'number' ? String(d[k]) : d[k] ? '1' : '');
  return [
    `p${pagesLen}`,
    pick('imageUrl'), pick('videoUrl'), pick('audioUrl'), pick('output'),
    pick('revisedPrompt'), pick('lastResult'), pick('transcription'),
  ].join('|');
}

/** Job statuses that mean a generation is still in flight. */
/**
 * True while a (paid) generation is in flight on the node. Undo/redo never
 * removes such a node: the run would finish into a missing id and the credits
 * would buy nothing.
 *
 * Only the AUTHORITATIVE signal counts (`isRunning`: the generation lifecycle
 * registry, the flow runner, active job pollers). Run flags in node data
 * (`isProcessing`, `streamingInProgress`, `jobStatus`...) are advisory: they
 * are copied by paste/duplicate and left behind by failure paths, and a stale
 * one would block undo forever.
 */
export function isNodeRunning(node: Node, isRunning?: (id: string) => boolean): boolean {
  return !!isRunning?.(node.id);
}

/** True when the node already holds a generation result. */
export function holdsResult(node: Node, isGenerator: (t: string | undefined) => boolean): boolean {
  return resultSignature(node, isGenerator) !== resultSignature({ ...node, data: {} } as Node, isGenerator);
}

/** `n` with every RESULT_DATA_KEY reset to the value it has in `ref`. */
function freezeResultKeys(n: Node, ref: Node): Node {
  const rd = (ref.data || {}) as Record<string, unknown>;
  const nd = (n.data || {}) as Record<string, unknown>;
  let data: Record<string, unknown> | null = null;
  for (const k of RESULT_DATA_KEYS) {
    if (rd[k] === nd[k]) continue;
    if (!data) data = { ...nd };
    setOrDelete(data, k, rd[k]);
  }
  return data ? ({ ...n, data } as Node) : n;
}

/**
 * The part of `current` that a just-closed window may attribute to the user
 * action. Protects against a generation that lands inside the window:
 *   - result fields of pre-existing nodes keep their `before` values, except on
 *     the nodes the action declared in `touchesResults`;
 *   - a NEW node that already holds a result and is fed by a pre-existing node
 *     is a generation delivery (the Output node deliverMediaOutput spawns), not
 *     part of the action — it and its edges are left out.
 */
export function protectCapture(
  before: FlowGraph,
  current: FlowGraph,
  touchesResults: ReadonlySet<string>,
  isGenerator: (t: string | undefined) => boolean,
): FlowGraph {
  const beforeNodes = new Map(before.nodes.map((n) => [n.id, n]));
  const delivered = new Set<string>();
  for (const n of current.nodes) {
    if (beforeNodes.has(n.id) || !holdsResult(n, isGenerator)) continue;
    if (current.edges.some((e) => e.target === n.id && beforeNodes.has(e.source))) delivered.add(n.id);
  }
  let changed = delivered.size > 0;
  const nodes = current.nodes
    .filter((n) => !delivered.has(n.id))
    .map((n) => {
      const b = beforeNodes.get(n.id);
      if (!b || touchesResults.has(n.id) || b === n) return n;
      const f = freezeResultKeys(n, b);
      if (f !== n) changed = true;
      return f;
    });
  if (!changed) return current;
  const edges = delivered.size
    ? current.edges.filter((e) => !delivered.has(e.source) && !delivered.has(e.target))
    : current.edges;
  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Three-way transition
// ---------------------------------------------------------------------------

function setOrDelete(obj: Record<string, unknown>, key: string, value: unknown) {
  if (value === undefined) delete obj[key];
  else obj[key] = value;
}

/**
 * Merge the change base->target into `cur` for one node. Returns `cur` itself
 * when nothing applies, so untouched nodes keep their identity.
 */
function mergeNode(cur: Node, base: Node, target: Node): Node {
  let out: Record<string, unknown> | null = null;
  const nodeKeys = new Set([...userNodeKeys(base), ...userNodeKeys(target)]);
  for (const k of nodeKeys) {
    const bv = (base as any)[k];
    const tv = (target as any)[k];
    if (deepEqual(bv, tv)) continue;
    if (!deepEqual((cur as any)[k], bv)) continue; // changed since → current wins
    if (!out) out = { ...cur };
    setOrDelete(out, k, tv);
  }

  const bd = (base.data || {}) as Record<string, unknown>;
  const td = (target.data || {}) as Record<string, unknown>;
  const cd = (cur.data || {}) as Record<string, unknown>;
  let data: Record<string, unknown> | null = null;
  if (bd !== td) {
    const dkeys = new Set([...userDataKeys(bd), ...userDataKeys(td)]);
    for (const k of dkeys) {
      const bv = bd[k];
      const tv = td[k];
      if (deepEqual(bv, tv)) continue;
      // `parameters` is merged per setting so a model change and an unrelated
      // setting written since do not clobber each other.
      if (k === 'parameters' && isPlainObject(bv) && isPlainObject(tv) && isPlainObject(cd[k])) {
        const cp = cd[k] as Record<string, unknown>;
        let np: Record<string, unknown> | null = null;
        const pkeys = new Set([...Object.keys(bv), ...Object.keys(tv)]);
        for (const pk of pkeys) {
          if (deepEqual(bv[pk], tv[pk])) continue;
          if (!deepEqual(cp[pk], bv[pk])) continue;
          if (!np) np = { ...cp };
          setOrDelete(np, pk, tv[pk]);
        }
        if (np) {
          if (!data) data = { ...cd };
          data[k] = np;
        }
        continue;
      }
      if (!deepEqual(cd[k], bv)) continue;
      if (!data) data = { ...cd };
      setOrDelete(data, k, tv);
    }
  }
  if (!out && !data) return cur;
  const result = (out || { ...cur }) as unknown as Node;
  if (data) (result as any).data = data;
  return result;
}

function mergeEdge(cur: Edge, base: Edge, target: Edge): Edge {
  let out: Record<string, unknown> | null = null;
  const keys = new Set([...edgeKeys(base), ...edgeKeys(target)]);
  for (const k of keys) {
    const bv = (base as any)[k];
    const tv = (target as any)[k];
    if (deepEqual(bv, tv)) continue;
    if (!deepEqual((cur as any)[k], bv)) continue;
    if (!out) out = { ...cur };
    setOrDelete(out, k, tv);
  }
  return (out as unknown as Edge) || cur;
}

function freshNode(n: Node): Node {
  const copy: any = { ...n, selected: false };
  delete copy.dragging;
  delete copy.resizing;
  return copy as Node;
}

/** Insert `item` after the closest preceding id (in `order`) already present in `list`. */
function insertInOrder<T extends { id: string }>(list: T[], item: T, order: string[]): void {
  const idx = order.indexOf(item.id);
  for (let i = idx - 1; i >= 0; i--) {
    const at = list.findIndex((x) => x.id === order[i]);
    if (at >= 0) {
      list.splice(at + 1, 0, item);
      return;
    }
  }
  if (idx === 0) list.unshift(item);
  else list.push(item);
}

/**
 * The node half of a transition, applied to whatever the nodes are NOW — so
 * it can run inside a `setNodes(cur => ...)` updater and never overwrite a
 * write (a generation result) that landed after the undo was requested.
 * `blocked` collects nodes the transition would remove but must keep because a
 * generation is running on them.
 */
export function transitionNodes(
  currentNodes: Node[],
  base: FlowGraph,
  target: FlowGraph,
  isGenerator: (t: string | undefined) => boolean = () => false,
  isRunning?: (id: string) => boolean,
  blocked?: string[],
): Node[] {
  const baseNodes = new Map(base.nodes.map((n) => [n.id, n]));
  const targetNodes = new Map(target.nodes.map((n) => [n.id, n]));
  const curNodeIds = new Set(currentNodes.map((n) => n.id));

  const nodes: Node[] = [];
  for (const cur of currentNodes) {
    const b = baseNodes.get(cur.id);
    if (!b) {
      nodes.push(cur); // created outside this transition (e.g. by a generation)
      continue;
    }
    const t = targetNodes.get(cur.id);
    if (t) {
      nodes.push(mergeNode(cur, b, t));
      continue;
    }
    // The transition removes this node. Keep it if it acquired a result since
    // (silently), or if a generation is running on it right now (blocked).
    if (resultSignature(cur, isGenerator) !== resultSignature(b, isGenerator)) {
      nodes.push(cur);
    } else if (isNodeRunning(cur, isRunning)) {
      nodes.push(cur);
      blocked?.push(cur.id);
    }
  }
  const targetOrder = target.nodes.map((n) => n.id);
  for (const t of target.nodes) {
    if (!baseNodes.has(t.id) && !curNodeIds.has(t.id)) {
      insertInOrder(nodes, freshNode(t), targetOrder);
    }
  }
  return nodes;
}

/** The edge half of a transition; drops edges whose endpoint is not in `nodeIds`. */
export function transitionEdges(
  currentEdges: Edge[],
  base: FlowGraph,
  target: FlowGraph,
  nodeIds: ReadonlySet<string>,
): Edge[] {
  const baseEdges = new Map(base.edges.map((e) => [e.id, e]));
  const targetEdges = new Map(target.edges.map((e) => [e.id, e]));
  const curEdgeIds = new Set(currentEdges.map((e) => e.id));
  const edges: Edge[] = [];
  for (const cur of currentEdges) {
    const b = baseEdges.get(cur.id);
    if (!b) {
      edges.push(cur);
      continue;
    }
    const t = targetEdges.get(cur.id);
    if (t) edges.push(mergeEdge(cur, b, t));
    // else: the transition removes this edge.
  }
  const targetEdgeOrder = target.edges.map((e) => e.id);
  for (const t of target.edges) {
    if (!baseEdges.has(t.id) && !curEdgeIds.has(t.id)) {
      insertInOrder(edges, { ...t, selected: false }, targetEdgeOrder);
    }
  }

  return edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
}

/**
 * Apply the transition `base -> target` to `current` (undo: after->before,
 * redo: before->after). See the module comment for the rules.
 */
export function applyTransition(
  current: FlowGraph,
  base: FlowGraph,
  target: FlowGraph,
  isGenerator: (t: string | undefined) => boolean = () => false,
  isRunning?: (id: string) => boolean,
  blocked?: string[],
): FlowGraph {
  const nodes = transitionNodes(current.nodes, base, target, isGenerator, isRunning, blocked);
  const edges = transitionEdges(current.edges, base, target, new Set(nodes.map((n) => n.id)));
  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Stacks, windows, coalescing
// ---------------------------------------------------------------------------

export class FlowHistory {
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private pending: PendingEntry | null = null;
  private seq = 0;
  private readonly limit: number;
  private readonly coalesceMs: number;
  private readonly windowMs: number;
  private readonly maxHoldMs: number;
  private readonly now: () => number;
  private readonly isGenerator: (t: string | undefined) => boolean;
  private readonly isRunning?: (id: string) => boolean;

  constructor(opts: FlowHistoryOptions = {}) {
    this.limit = opts.limit ?? 100;
    this.coalesceMs = opts.coalesceMs ?? 600;
    this.windowMs = opts.windowMs ?? 1000;
    this.maxHoldMs = opts.maxHoldMs ?? 60_000;
    this.now = opts.now ?? (() => Date.now());
    this.isGenerator = opts.isGenerator ?? (() => false);
    this.isRunning = opts.isRunning;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get undoDepth(): number {
    return this.undoStack.length;
  }

  get redoDepth(): number {
    return this.redoStack.length;
  }

  get hasPending(): boolean {
    return this.pending !== null;
  }

  /** A held gesture (drag, delete animation, auto-arrange) is still in progress. */
  get isBusy(): boolean {
    return !!this.pending?.hold || !!this.peekUndo()?.hold;
  }

  peekUndo(): HistoryEntry | undefined {
    return this.undoStack[this.undoStack.length - 1];
  }

  peekRedo(): HistoryEntry | undefined {
    return this.redoStack[this.redoStack.length - 1];
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.pending = null;
  }

  /** Call BEFORE a user action mutates the graph. */
  checkpoint(current: FlowGraph, label: string, opts: CheckpointOptions = {}): void {
    const now = this.now();
    // A window that outlived its action without an effect is stale: never let
    // it swallow this new action.
    if (this.pending && this.isStale(this.pending, now)) this.pending = null;
    // Several entry points can fire for one gesture (React Flow's Delete key
    // and ours, add-node + auto-connect): everything before the first effect
    // lands is one step.
    if (this.pending) {
      opts.touchesResults?.forEach((id) => this.pending!.touchesResults.add(id));
      if (opts.hold) {
        this.pending.hold = true;
        // The held gesture owns the step, so its release() must find it.
        if (opts.coalesceKey) this.pending.coalesceKey = opts.coalesceKey;
        this.pending.label = label;
      }
      return;
    }
    const top = this.peekUndo();
    if (
      opts.coalesceKey &&
      top &&
      top.coalesceKey === opts.coalesceKey &&
      !top.sealed &&
      this.redoStack.length === 0 &&
      (top.hold || now - top.lastTouch <= this.coalesceMs)
    ) {
      top.lastTouch = now;
      if (opts.hold) top.hold = true;
      opts.touchesResults?.forEach((id) => top.touchesResults.add(id));
      return;
    }
    this.pending = {
      id: ++this.seq,
      label,
      coalesceKey: opts.coalesceKey,
      before: current,
      openedAt: now,
      hold: !!opts.hold,
      touchesResults: new Set(opts.touchesResults ?? []),
    };
  }

  private isStale(p: PendingEntry, now: number): boolean {
    return now - p.openedAt >= (p.hold ? this.maxHoldMs : this.windowMs);
  }

  /** End a held step (drag stop). A short grace keeps the final commit. */
  release(coalesceKey?: string): void {
    const now = this.now();
    if (this.pending && (!coalesceKey || this.pending.coalesceKey === coalesceKey)) {
      // Re-arm: from now on the window expires like any un-held one, so a held
      // gesture that changed nothing (an idempotent auto-arrange) cannot stay
      // open and swallow a later, unrelated commit.
      this.pending.hold = false;
      this.pending.openedAt = now;
    }
    const top = this.peekUndo();
    if (top && (!coalesceKey || top.coalesceKey === coalesceKey) && top.hold) {
      top.hold = false;
      top.sealed = true;
      top.lastTouch = now;
      top.liveUntil = now + 300;
    }
  }

  /** Feed every committed graph. Closes the open window / refreshes a live step. */
  observe(current: FlowGraph): void {
    const now = this.now();
    if (this.pending && this.isStale(this.pending, now)) {
      // Too late to be the action's own effect: whatever this commit is, it
      // is not attributable to the action.
      this.pending = null;
    }
    if (this.pending) {
      if (graphsEqual(current, this.pending.before)) return;
      const p = this.pending;
      const after = protectCapture(p.before, current, p.touchesResults, this.isGenerator);
      // Only a generation changed (a result field, a delivered Output node):
      // the action's own effect has not landed yet — keep waiting.
      if (graphsEqual(after, p.before)) return;
      this.pending = null;
      const entry: HistoryEntry = {
        id: p.id,
        label: p.label,
        coalesceKey: p.coalesceKey,
        before: p.before,
        after,
        afterNodeIds: new Set(after.nodes.map((n) => n.id)),
        afterEdgeIds: new Set(after.edges.map((e) => e.id)),
        lastTouch: now,
        hold: p.hold,
        touchesResults: p.touchesResults,
        sealed: !p.coalesceKey && !p.hold,
        liveUntil: 0,
      };
      this.undoStack.push(entry);
      if (this.undoStack.length > this.limit) this.undoStack.splice(0, this.undoStack.length - this.limit);
      this.redoStack = [];
      return;
    }
    const top = this.peekUndo();
    if (!top) return;
    const live = top.hold || (!top.sealed && now - top.lastTouch <= this.coalesceMs) || now <= top.liveUntil;
    if (!live) return;
    top.after = this.refreshAfter(top, current);
  }

  /** Drop a window that never saw its effect (call on a timer). */
  expire(_current?: FlowGraph): void {
    if (this.pending && this.isStale(this.pending, this.now())) this.pending = null;
  }

  /**
   * Steps whose merge changes nothing (everything they touched was changed
   * since, e.g. a blob URL swapped for the R2 one) are passed over — moved to
   * the other stack — and the next one is tried, so Ctrl+Z always does
   * something visible or nothing at all.
   */
  undo(current: FlowGraph): ApplyResult | null {
    this.settle(current);
    return this.step(current, this.undoStack, this.redoStack, 'after', 'before');
  }

  redo(current: FlowGraph): ApplyResult | null {
    this.settle(current);
    return this.step(current, this.redoStack, this.undoStack, 'before', 'after');
  }

  /**
   * Pop from `from` until a step changes something. A step blocked by a
   * running generation is NOT skipped: it stays where it is and is returned
   * with `blocked`, so the user is told instead of silently undoing an older
   * step. Steps whose merge changes nothing are passed over silently.
   */
  private step(
    current: FlowGraph,
    from: HistoryEntry[],
    to: HistoryEntry[],
    baseKey: 'before' | 'after',
    targetKey: 'before' | 'after',
  ): ApplyResult | null {
    let entry: HistoryEntry | undefined;
    while ((entry = from.pop())) {
      const base = entry[baseKey];
      const target = entry[targetKey];
      const blocked: string[] = [];
      const graph = applyTransition(current, base, target, this.isGenerator, this.isRunning, blocked);
      if (blocked.length) {
        from.push(entry);
        return { graph: current, entry, base, target, blocked: true };
      }
      to.push(entry);
      if (!graphsEqual(graph, current)) return { graph, entry, base, target };
    }
    return null;
  }

  /** Close any open window and stop every live step before moving through history. */
  private settle(current: FlowGraph): void {
    if (this.pending) {
      this.observe(current);
      this.pending = null; // no attributable effect yet: the action changed nothing
    }
    for (const e of this.undoStack) {
      e.hold = false;
      e.sealed = true;
      e.liveUntil = 0;
    }
  }

  private refreshAfter(entry: HistoryEntry, current: FlowGraph): FlowGraph {
    const prev = new Map(entry.after.nodes.map((n) => [n.id, n]));
    const nodes = current.nodes
      .filter((n) => entry.afterNodeIds.has(n.id))
      .map((n) => {
        const old = prev.get(n.id);
        if (!old || old === n || entry.touchesResults.has(n.id)) return n;
        return freezeResultKeys(n, old);
      });
    // A new edge joins a live step only when both ends belong to the step (e.g.
    // Iris wiring its own nodes a tick later) — never an edge from a generator
    // to an Output node it just spawned.
    const edges = current.edges.filter(
      (e) => entry.afterEdgeIds.has(e.id) || (entry.afterNodeIds.has(e.source) && entry.afterNodeIds.has(e.target)),
    );
    return { nodes, edges };
  }
}

// ---------------------------------------------------------------------------
// Keyboard
// ---------------------------------------------------------------------------

export type HistoryShortcut = 'undo' | 'redo' | null;

/** Ctrl/Cmd+Z → undo; Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y → redo. */
export function getHistoryShortcut(e: {
  key: string;
  code?: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): HistoryShortcut {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
  const key = (e.key || '').toLowerCase();
  const isZ = key === 'z' || (key.length !== 1 && e.code === 'KeyZ');
  const isY = key === 'y' || (key.length !== 1 && e.code === 'KeyY');
  if (isZ) return e.shiftKey ? 'redo' : 'undo';
  if (isY && !e.shiftKey) return 'redo';
  return null;
}

/**
 * True while a modal editor that owns its own keys is open (the inpaint mask
 * editor has its own undo; the Lightbox already stops keys at window capture).
 */
export function isCanvasHistoryBlocked(doc: Document | undefined = typeof document !== 'undefined' ? document : undefined): boolean {
  if (!doc) return false;
  return !!doc.querySelector('.inpaint-editor-modal, [aria-modal="true"]');
}

/**
 * True when the key belongs to a text field — there Ctrl+Z is the browser's
 * own text undo and must never reach the canvas.
 */
const TEXT_INPUT_TYPES: ReadonlySet<string> = new Set(['text', 'search', 'url', 'email', 'password', 'tel', 'number']);

export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el !== 'object') return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'TEXTAREA') return true;
  // Only text-entry inputs have a native text undo. A slider, checkbox or
  // select keeps focus after a change, and Ctrl+Z there must undo that change
  // on the canvas. `number` is typed into (and has native undo), so it stays.
  if (tag === 'INPUT') return TEXT_INPUT_TYPES.has(((el as HTMLInputElement).type || 'text').toLowerCase());
  if (el.isContentEditable) return true;
  if (typeof el.closest === 'function' && el.closest('[contenteditable=""], [contenteditable="true"]')) return true;
  return false;
}
